import React, { useEffect, useState } from "react"
import MarkAsDone from "../../../components/Orders/Helpers/MarkAsDone"
import ComplaintDrawer from "../../../components/Orders/ComplaintDrawer"
import { Button, Col, Descriptions, message, Popover, Row, Space, Table, Typography } from "antd"
import {
  CheckSquareFilled,
  CloseOutlined,
  FileOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { endDate, startDate } from "../../../components/Orders/Helpers/TableDetails"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import moment from "moment"

const { Text, Link } = Typography

export default function WarehouseOrders() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [removingOrderIds, setRemovingOrderIds] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const selectedOrder = selectedRows.length === 1 ? selectedRows[0] : null
  const exactlyOneSelected = selectedRows.length === 1

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows) },
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  }

  const handleInvoiceDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        invoiceDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handlePackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        packingListDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handleCustomPackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, { "customPackingListData.downloaded": true }) // Use dot notation
      // Update local state
      setData((prevData) =>
        prevData.map((item) =>
          item.id === recordId && item.customPackingListData
            ? {
                ...item,
                customPackingListData: { ...item.customPackingListData, downloaded: true },
              }
            : item,
        ),
      )
    } catch (error) {
      console.error("Error updating Firestore document (custom PL): ", error)
      message.error("Failed to mark custom packing list as downloaded.")
    }
  }

  useEffect(() => {
    // Set loading
    setLoading(true)
    let ref = query(
      collection(db, "orders"),
      where("orderStatus", "==", 6),
      where("hasInvoice", "==", true),
    )

    const unsubscribe = onSnapshot(ref, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      console.log(results)
      // Update state with plate data
      setData(results)

      // Set loading to false
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // Function to mark an order as done and start animation
  const markOrderAsDone = (orderId) => {
    // Trigger the fade-out animation
    setRemovingOrderIds((prevIds) => [...prevIds, orderId])

    // Wait for the animation to complete before removing the item
    setTimeout(() => {
      setData((prevData) => prevData.filter((item) => item.id !== orderId))
      setRemovingOrderIds((prevIds) => prevIds.filter((id) => id !== orderId))
    }, 1000) // Ensure this matches the CSS animation duration
  }

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  const returnCorrectButton = (record) => {
    const mpDrukaOrder = record?.manufacturer?.company === "Mp drukA SIA"
    if (mpDrukaOrder) {
      return <CloseOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
    }

    const hasInvoice = record?.hasInvoice ?? false
    const invoiceData = record?.invoice
    const invoiceUrl = invoiceData?.invoiceUrl

    const hasPackingList = record?.hasPackingList ?? false
    const packingListUrl = record?.packingListUrl

    const hasCustomPackingList = record?.customPackingList ?? false
    const customPLData = record?.customPackingListData
    const customPLUrl = customPLData?.documentDownloadUrl

    let displayedSomething = false

    return (
      <Space direction="vertical" align="start" size={4}>
        {/* Invoice */}
        {hasInvoice &&
          invoiceUrl &&
          (() => {
            displayedSomething = true
            return (
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleInvoiceDownload(record.id)}>
                <Button size="small" icon={<FileOutlined style={{ color: "#1677ff" }} />}>
                  {invoiceData.name || "Invoice"}
                  {record.invoiceDownloaded && (
                    <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
                  )}
                </Button>
              </a>
            )
          })()}

        {/* Packing List */}
        {hasPackingList &&
          packingListUrl &&
          (() => {
            displayedSomething = true
            return (
              <a
                href={packingListUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handlePackingListDownload(record.id)}>
                <Button size="small" icon={<FileOutlined style={{ color: "#52c41a" }} />}>
                  Packing List
                  {record.packingListDownloaded && (
                    <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
                  )}
                </Button>
              </a>
            )
          })()}

        {/* Custom PL */}
        {hasCustomPackingList &&
          customPLUrl &&
          (() => {
            displayedSomething = true
            return (
              <a
                href={customPLUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCustomPackingListDownload(record.id)}>
                <Button size="small" icon={<FileOutlined style={{ color: "#faad14" }} />}>
                  Custom PL
                  {customPLData?.downloaded && (
                    <CheckSquareFilled style={{ marginLeft: 4, color: "#1677ff" }} />
                  )}
                </Button>
              </a>
            )
          })()}

        {!displayedSomething && <Text type="secondary">No Documents</Text>}
      </Space>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      width: "10%",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: true,
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "15%",
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Delivery",
      dataIndex: "documentFor",
      align: "center",
      ellipsis: true,
      width: "10%",
      render: (value) => {
        if (value === "clientPickup") {
          return "Client Pickup"
        } else if (value === "ourDilvery") {
          return "Our Delivery"
        } else {
          return "Warehouse Logistics Pickup"
        }
      },
    },
    {
      title: "Box",
      dataIndex: "boxesCount",
      align: "center",
      width: "3%",
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text>{record.boxCount}</Text>
            </>
          )
        } else {
          return (
            <>
              <Text>{value}</Text>
            </>
          )
        }
      },
    },
    {
      title: "Count",
      dataIndex: "amountCreated",
      align: "center",
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text copyable={{ text: record.rollCount }}>{record.rollCount} Rolls</Text>
            </>
          )
        } else {
          return (
            <>
              <Popover
                content={
                  <>
                    <div style={{ padding: 5 }}>
                      <Descriptions column={1} style={{ width: 150 }}>
                        <Descriptions.Item label="Ordered">{record.labelCount}</Descriptions.Item>
                        <Descriptions.Item label="Created">{value}</Descriptions.Item>
                      </Descriptions>
                    </div>
                  </>
                }
                title="Amount details"
                trigger="click">
                <Link
                // copyable={{ text: value }}
                >
                  {value}
                </Link>
              </Popover>
            </>
          )
        }
      },
    },
    {
      title: "Invoice",
      align: "center",
      width: "8%",
      render: (value, record) => {
        return returnCorrectButton(record) // Add the return keyword here
      },
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => {
        if (value) {
          return (
            <Link target="_blank" href={value}>
              Link
            </Link>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      width: "5%",
    },
    {
      title: "Started",
      dataIndex: "created",
      align: "center",
      width: "7%",

      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => startDate(value),
    },
    {
      title: "Ended",
      align: "center",
      dataIndex: "doneByRewinder",
      width: "7%",
      sorter: (a, b) => a.doneByRewinder.seconds - b.doneByRewinder.seconds,
      render: (value, record) => endDate(value, record),
    },
    // {
    //   title: "Due date",
    //   dataIndex: "orderDueDate",
    //   align: "center",
    //   sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
    //   render: (value) => <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>,
    // },
    {
      title: "Order Done?",
      align: "center",
      render: (record) => (
        <MarkAsDone order={record} doneDate={moment()} onDone={() => markOrderAsDone(record.id)} />
        // <button onClick={() => console.log(record)}>log</button>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<ShoppingCartOutlined />} text="Warehouse Orders" />
        </Col>
        <Col span={24}>
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 8 }}>
            <ComplaintDrawer
              orderData={selectedOrder}
              disabled={!exactlyOneSelected}
              buttonText="Create Complaint"
            />
          </div>
          <Table
            style={{ marginTop: "15px" }}
            size="small"
            columns={defaultColumns}
            bordered
            loading={loading}
            dataSource={data}
            rowSelection={rowSelection}
            rowClassName={(record) => (removingOrderIds.includes(record.id) ? "fade-out" : "")}
            rowKey={(record) => record.id}
            pagination={{ defaultPageSize: "12", showSizeChanger: true }}
          />
        </Col>
      </Row>
    </div>
  )
}
